/**
 * 🟦 V14.1.2 — Modal "Proponer al Marketplace" multi-modo.
 *
 * Soporta 3 modos:
 *   Modo 1 (Lyon):     recibe `recipe` (objeto). Payload readonly.
 *   Modo 2 (Nueva):    recibe ningún initial. Payload editable JSON.
 *   Modo 3 (Reintento): recibe `initialTitle`, `initialDescription`,
 *                        `initialPayload`. Todo editable.
 *
 * Props:
 *   - open
 *   - recipe              (modo 1) — objeto receta
 *   - initialTitle        (modo 2/3)
 *   - initialDescription  (modo 2/3)
 *   - initialPayload      (modo 2/3) — dict o null
 *   - editablePayload     bool — default true si NO hay `recipe`
 *   - onClose()
 *   - onSuccess(submission)
 *
 * Endpoint: POST /api/submissions
 */
import { useEffect, useMemo, useState } from 'react';
import { Send, X, Loader2, CheckCircle2, AlertTriangle, ChevronDown, ChevronRight } from 'lucide-react';
import { API_URL } from '../../config/api';

const ProposeRecipeModal = ({
  open,
  recipe = null,
  initialTitle = '',
  initialDescription = '',
  initialPayload = null,
  editablePayload = null, // null = auto
  onClose,
  onSuccess,
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [payloadText, setPayloadText] = useState('');
  const [payloadCollapsed, setPayloadCollapsed] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);

  const isPayloadEditable = editablePayload === null ? !recipe : Boolean(editablePayload);

  useEffect(() => {
    if (!open) return;
    // Decide initial values based on mode.
    const baseTitle = (recipe?.name || recipe?.title || initialTitle || '').toString();
    const baseDesc  = (recipe?.description || initialDescription || '').toString();
    const basePayload = recipe || initialPayload || null;
    setTitle(baseTitle);
    setDescription(baseDesc);
    setPayloadText(basePayload ? JSON.stringify(basePayload, null, 2) : '');
    setPayloadCollapsed(!isPayloadEditable); // Modo nueva/retry → expandido; lyon → colapsado.
    setToast(null);
  }, [open, recipe, initialTitle, initialDescription, initialPayload, isPayloadEditable]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const { parsedPayload, payloadError } = useMemo(() => {
    if (!payloadText.trim()) return { parsedPayload: null, payloadError: null };
    try {
      const p = JSON.parse(payloadText);
      if (typeof p !== 'object' || p === null) return { parsedPayload: null, payloadError: 'El payload debe ser un objeto JSON.' };
      return { parsedPayload: p, payloadError: null };
    } catch (e) {
      return { parsedPayload: null, payloadError: 'JSON inválido.' };
    }
  }, [payloadText]);

  const showToast = (kind, msg) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 4000);
  };

  const canSubmit =
    title.trim().length >= 3 &&
    parsedPayload !== null &&
    !payloadError &&
    !submitting;

  const handleSubmit = async () => {
    if (!canSubmit) return;
    setSubmitting(true);
    try {
      // Inferimos type del payload: si tiene `theme` → pack; si tiene `steps` → recipe.
      const inferredType = Array.isArray(parsedPayload.steps) ? 'recipe' :
                           (parsedPayload.theme ? 'pack' : 'recipe');
      const res = await fetch(`${API_URL}/submissions`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          type: inferredType,
          title: title.trim(),
          description: description.trim(),
          payload: parsedPayload,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.status === 200 || res.status === 201) {
        showToast('ok', 'Propuesta enviada. Esperando aprobación.');
        if (onSuccess) onSuccess(data.submission);
        setTimeout(() => onClose(), 1100);
        return;
      }
      if (res.status === 409) {
        showToast('warn', 'Ya propusiste esta receta.');
        return;
      }
      if (res.status === 400 || res.status === 422) {
        showToast('err', `Datos inválidos: ${data?.detail || 'revisá los campos'}`);
        return;
      }
      throw new Error(data?.detail || `http_${res.status}`);
    } catch (e) {
      showToast('err', `No se pudo enviar la propuesta (${e?.message || 'error'}).`);
    } finally {
      setSubmitting(false);
    }
  };

  if (!open) return null;

  return (
    <div
      data-testid="propose-recipe-modal"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
      className="fixed inset-0 z-[10001] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
    >
      <div className="bg-slate-950 border border-cyan-500/30 rounded-2xl shadow-2xl w-full max-w-lg max-h-[88vh] overflow-hidden flex flex-col">
        <div className="px-5 py-3 border-b border-slate-800 flex items-center justify-between bg-gradient-to-r from-cyan-950/40 to-fuchsia-950/30">
          <h3 className="text-sm font-bold text-white flex items-center gap-2">
            <Send className="w-4 h-4 text-cyan-300" />
            Proponer al Marketplace
          </h3>
          <button
            type="button"
            onClick={onClose}
            data-testid="propose-recipe-close"
            disabled={submitting}
            className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white disabled:opacity-50"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Título *</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              maxLength={120}
              data-testid="propose-recipe-title"
              disabled={submitting}
              placeholder="Nombre que verán los demás usuarios"
              className="w-full mt-1 rounded-md bg-slate-900 border border-slate-700 focus:border-cyan-500 focus:outline-none px-2 py-1.5 text-[12px] text-white placeholder-slate-600 disabled:opacity-50"
            />
            <p className="text-[10px] text-slate-500 mt-0.5">
              {title.length}/120 · mínimo 3 caracteres
            </p>
          </div>

          <div>
            <label className="text-[10px] uppercase tracking-wider text-slate-500">Descripción (opcional)</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={3}
              maxLength={400}
              data-testid="propose-recipe-description"
              disabled={submitting}
              placeholder="¿Qué hace esta receta? ¿En qué nodo se aplica? ¿Qué resultado produce?"
              className="w-full mt-1 rounded-md bg-slate-900 border border-slate-700 focus:border-cyan-500 focus:outline-none px-2 py-1.5 text-[12px] text-white placeholder-slate-600 disabled:opacity-50"
            />
            <p className="text-[10px] text-slate-500 mt-0.5">{description.length}/400</p>
          </div>

          <div>
            <button
              type="button"
              onClick={() => setPayloadCollapsed((v) => !v)}
              data-testid="propose-recipe-payload-toggle"
              className="flex items-center gap-1 text-[10px] uppercase tracking-wider text-slate-500 hover:text-white"
            >
              {payloadCollapsed ? <ChevronRight className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
              Payload JSON {isPayloadEditable ? '(editable)' : '(readonly)'} *
            </button>
            {!payloadCollapsed && (
              <>
                <textarea
                  value={payloadText}
                  onChange={(e) => setPayloadText(e.target.value)}
                  readOnly={!isPayloadEditable}
                  data-testid="propose-recipe-payload"
                  disabled={submitting}
                  rows={8}
                  className={`w-full mt-1 rounded-md text-[10px] font-mono px-2 py-1.5 text-slate-200 disabled:opacity-50 ${
                    isPayloadEditable
                      ? 'bg-slate-900 border border-slate-700 focus:border-cyan-500 focus:outline-none'
                      : 'bg-slate-900/80 border border-slate-800 cursor-not-allowed'
                  } ${payloadError ? 'border-red-500/60' : ''}`}
                  placeholder={isPayloadEditable
                    ? 'Pegá aquí un JSON portable. Para recipe debe contener "steps":[...]. Para pack debe contener "theme":{...}.'
                    : ''}
                />
                {payloadError && (
                  <p className="text-[10px] text-red-400 mt-0.5" data-testid="propose-recipe-payload-error">
                    {payloadError}
                  </p>
                )}
              </>
            )}
          </div>
        </div>

        <div className="border-t border-slate-800 px-5 py-3 flex items-center justify-end gap-2 bg-slate-900/50">
          <button
            type="button"
            onClick={onClose}
            disabled={submitting}
            data-testid="propose-recipe-cancel"
            className="px-3 py-1.5 rounded-md bg-slate-800 hover:bg-slate-700 text-slate-300 text-[11px] disabled:opacity-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={!canSubmit}
            data-testid="propose-recipe-submit"
            className="px-3 py-1.5 rounded-md bg-cyan-600 hover:bg-cyan-500 disabled:opacity-40 disabled:cursor-not-allowed text-white text-[11px] font-semibold flex items-center gap-1"
          >
            {submitting ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />}
            Enviar propuesta
          </button>
        </div>

        {toast && (
          <div
            data-testid="propose-recipe-toast"
            className={`mx-3 mb-3 rounded-md px-3 py-2 text-[11px] font-semibold border flex items-center gap-2 ${
              toast.kind === 'ok' ? 'bg-emerald-900/40 text-emerald-100 border-emerald-500/50' :
              toast.kind === 'warn' ? 'bg-amber-900/40 text-amber-100 border-amber-500/50' :
              'bg-red-900/40 text-red-100 border-red-500/50'
            }`}
          >
            {toast.kind === 'ok' ? <CheckCircle2 className="w-3 h-3" /> : <AlertTriangle className="w-3 h-3" />}
            {toast.msg}
          </div>
        )}
      </div>
    </div>
  );
};

export default ProposeRecipeModal;
